<template>
  <div>
    <header>
      <h1>Locked Stories</h1>
      <p class="text-muted">Live view of stories currently open for editing.</p>
    </header>

    <LoaderOverlay :loading="loading">
      <div v-if="lockedStories.length === 0" class="alert alert-info">
        No stories are currently locked.
      </div>

      <table v-else class="table table-striped table-hover">
        <thead>
          <tr>
            <th>User</th>
            <th>Story</th>
            <th>Locked For</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="lockedStory in lockedStories" :key="lockedStory.id">
            <td>{{ lockedStory.locker?.name || 'Unknown user' }}</td>
            <td>
              <router-link :to="`/stories/${lockedStory.id}`">
                {{ storyLabel(lockedStory) }}
              </router-link>
              <div v-if="lockedStory.slug" class="text-muted small">{{ lockedStory.slug }}</div>
            </td>
            <td :title="formatDateTime(lockedStory.locked_at)">
              {{ formatElapsed(lockedStory.locked_at) }}
            </td>
          </tr>
        </tbody>
      </table>
    </LoaderOverlay>
  </div>
</template>

<script setup>
  import { getCurrentInstance, onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue'
  import { storeToRefs } from 'pinia'
  import LoaderOverlay from '@/components/shared/loader_overlay.vue'
  import { useLockedStoriesStore } from '@/stores/locked_stories'
  import { formatDateTime } from '@/utils/time_helpers.js'

  const instance = getCurrentInstance()
  const lockedStoriesStore = useLockedStoriesStore()
  const { lockedStories } = storeToRefs(lockedStoriesStore)
  const loading = ref(true)
  const elapsedTick = ref(Date.now())

  let cableSubscription = null
  let elapsedTimer = null

  const storyLabel = (lockedStory) => {
    return lockedStory.headline || lockedStory.slug || `Story #${lockedStory.id}`
  }

  const formatElapsed = (lockedAt) => {
    if (!lockedAt) return 'Unknown'

    const elapsedMs = Math.max(0, elapsedTick.value - new Date(lockedAt).getTime())
    const totalSeconds = Math.floor(elapsedMs / 1000)
    const totalMinutes = Math.floor(elapsedMs / 60000)

    if (totalSeconds < 60) return `${totalSeconds}s`

    if (totalMinutes < 60) {
      const seconds = totalSeconds % 60
      return `${totalMinutes}m ${seconds}s`
    }

    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    if (hours < 24) {
      return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`
    }

    const days = Math.floor(hours / 24)
    const remainingHours = hours % 24

    return remainingHours === 0 ? `${days}d` : `${days}d ${remainingHours}h`
  }

  const handleCableReceived = (data) => {
    if (data?.action !== 'lock') return

    lockedStoriesStore.applyLockEvent(data.story)
  }

  onBeforeMount(async () => {
    try {
      await lockedStoriesStore.fetch()
    } finally {
      loading.value = false
    }
  })

  onMounted(() => {
    const vm = instance?.proxy

    cableSubscription = vm?.$cable?.subscriptions.create(
      { channel: 'StoriesChannel' },
      { received: handleCableReceived }
    )

    elapsedTimer = window.setInterval(() => {
      elapsedTick.value = Date.now()
    }, 1000)
  })

  onBeforeUnmount(() => {
    cableSubscription?.unsubscribe()

    if (elapsedTimer) {
      window.clearInterval(elapsedTimer)
    }
  })
</script>